/* ==========================================================================
   Soleero collection products
   Native Shopify filters + staged drawer preview + Section Rendering API.
   ========================================================================== */

const HZ_COLLECTION_SELECTOR = "ar-collection-products[data-hz-collection-section]";

class ArCollectionProducts extends HTMLElement {
  constructor() {
    super();
    this.abortController = new AbortController();
    this.previewController = null;
    this.commitController = null;
    this.loadMoreController = null;
    this.previewTimer = 0;
    this.previewSequence = 0;
    this.commitSequence = 0;
    this.previewResponse = null;
    this.lastFailedAction = null;
    this.isApplying = false;
    this.isConnectedController = false;
    this.navigationResizeObserver = null;
  }

  connectedCallback() {
    if (this.isConnectedController) return;
    this.isConnectedController = true;
    if (this.abortController.signal.aborted) this.abortController = new AbortController();

    this.sectionId = this.dataset.hzCollectionSection;
    this.drawer = document.getElementById(`hz__collectionDrawer-${this.sectionId}`);
    this.appliedUrl = this.cleanUrl(new URL(window.location.href));
    this.visibleCount = Number(this.dataset.hzCollectionVisible || 0);
    this.totalCount = Number(this.dataset.hzCollectionTotal || 0);
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    this.addEventListener("click", this.handleRootClick, {
      signal: this.abortController.signal
    });
    this.addEventListener("change", this.handleRootChange, {
      signal: this.abortController.signal
    });
    this.addEventListener("keydown", this.handleRootKeydown, {
      signal: this.abortController.signal
    });
    document.addEventListener("pointerdown", this.handleDocumentPointerDown, {
      signal: this.abortController.signal
    });
    window.addEventListener("popstate", this.handlePopState, {
      signal: this.abortController.signal
    });

    if ("ResizeObserver" in window) {
      this.navigationResizeObserver = new ResizeObserver(() => {
        this.positionNavigationIndicator({ animate: false });
      });
      this.navigationResizeObserver.observe(this);
    }

    this.refreshEnhancedUi({ centerNavigation: true });

    if (this.drawer) {
      this.drawer.addEventListener("click", this.handleDrawerClick, {
        signal: this.abortController.signal
      });
      this.drawer.addEventListener("change", this.handleDrawerChange, {
        signal: this.abortController.signal
      });
      this.drawer.addEventListener("input", this.handleDrawerInput, {
        signal: this.abortController.signal
      });
      this.drawer.addEventListener("submit", this.handleDrawerSubmit, {
        signal: this.abortController.signal
      });
      this.drawer.addEventListener("dialog:before-show", this.handleDrawerBeforeShow, {
        signal: this.abortController.signal
      });
      this.drawer.addEventListener("dialog:after-hide", this.handleDrawerAfterHide, {
        signal: this.abortController.signal
      });

      this.drawerForm = this.drawer.querySelector("[data-hz-collection-filters-form]");
      this.captureAppliedDrawerState();
      this.drawer.setAttribute("data-hz-collection-ready", "true");
    }
  }

  disconnectedCallback() {
    this.isConnectedController = false;
    this.abortController.abort();
    this.abortPendingRequests();
    window.clearTimeout(this.previewTimer);
    this.navigationResizeObserver?.disconnect();
    this.navigationResizeObserver = null;

    if (this.drawer?.parentElement === document.body) {
      this.drawer.remove();
    }
  }

  handleRootClick = (event) => {
    const sortLabel = event.target.closest?.(".hz__collectionSortLabel");
    if (sortLabel && this.contains(sortLabel) && this.classList.contains("hz__collectionProducts--sortEnhanced")) {
      const form = sortLabel.closest("[data-hz-collection-sort-form]");
      const trigger = form?.querySelector("[data-hz-sort-trigger]");
      if (trigger) {
        event.preventDefault();
        trigger.focus();
        return;
      }
    }

    const sortTrigger = event.target.closest?.("[data-hz-sort-trigger]");
    if (sortTrigger && this.contains(sortTrigger)) {
      event.preventDefault();
      const control = sortTrigger.closest("[data-hz-sort-control]");
      this.setSortMenuOpen(control, sortTrigger.getAttribute("aria-expanded") !== "true");
      return;
    }

    const sortOption = event.target.closest?.("[data-hz-sort-option]");
    if (sortOption && this.contains(sortOption)) {
      event.preventDefault();
      this.selectSortOption(sortOption);
      return;
    }

    const link = event.target.closest?.(
      "[data-hz-collection-commit-link], [data-hz-collection-page-link], [data-hz-collection-load-more]"
    );
    if (!link || !this.contains(link) || !this.isPlainPrimaryClick(event)) return;

    if (link.matches("[data-hz-collection-load-more]")) {
      event.preventDefault();
      this.loadMore(link, { moveFocus: event.detail === 0 });
      return;
    }

    event.preventDefault();
    this.commitUrl(new URL(link.href, window.location.href), {
      historyMode: "push",
      scroll: true,
      focusTarget: "results"
    });
  };

  handleRootKeydown = (event) => {
    const trigger = event.target.closest?.("[data-hz-sort-trigger]");
    if (trigger && this.contains(trigger)) {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        const control = trigger.closest("[data-hz-sort-control]");
        this.setSortMenuOpen(control, true, {
          focusOption: true,
          focusLast: event.key === "ArrowUp"
        });
      } else if (event.key === "Escape") {
        this.setSortMenuOpen(trigger.closest("[data-hz-sort-control]"), false);
      }
      return;
    }

    const option = event.target.closest?.("[data-hz-sort-option]");
    if (!option || !this.contains(option)) return;

    const control = option.closest("[data-hz-sort-control]");
    const options = Array.from(control?.querySelectorAll("[data-hz-sort-option]") || []);
    const currentIndex = options.indexOf(option);
    if (currentIndex < 0) return;

    if (event.key === "ArrowDown" || event.key === "ArrowUp" || event.key === "Home" || event.key === "End") {
      event.preventDefault();
      let nextIndex = currentIndex;
      if (event.key === "ArrowDown") nextIndex = Math.min(options.length - 1, currentIndex + 1);
      if (event.key === "ArrowUp") nextIndex = Math.max(0, currentIndex - 1);
      if (event.key === "Home") nextIndex = 0;
      if (event.key === "End") nextIndex = options.length - 1;
      options[nextIndex]?.focus();
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      this.setSortMenuOpen(control, false, { restoreFocus: true });
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.selectSortOption(option);
      return;
    }

    if (event.key === "Tab") {
      this.setSortMenuOpen(control, false);
    }
  };

  handleDocumentPointerDown = (event) => {
    const openTrigger = this.querySelector('[data-hz-sort-trigger][aria-expanded="true"]');
    if (!openTrigger) return;
    const control = openTrigger.closest("[data-hz-sort-control]");
    if (control?.contains(event.target)) return;
    this.setSortMenuOpen(control, false);
  };

  handleRootChange = (event) => {
    const sortSelect = event.target.closest("[data-hz-collection-sort]");
    if (!sortSelect || !this.contains(sortSelect)) return;

    const url = new URL(window.location.href);
    if (sortSelect.value && sortSelect.value !== sortSelect.dataset.hzDefaultSort) {
      url.searchParams.set("sort_by", sortSelect.value);
    } else {
      url.searchParams.delete("sort_by");
    }
    url.searchParams.delete("page");

    this.commitUrl(url, {
      historyMode: "push",
      scroll: true,
      focusTarget: "sort"
    });
  };

  refreshEnhancedUi({ centerNavigation = false } = {}) {
    const hasEnhancedSort = Boolean(this.querySelector("[data-hz-sort-custom]"));
    this.classList.toggle("hz__collectionProducts--sortEnhanced", hasEnhancedSort);

    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.positionNavigationIndicator({ animate: false, center: centerNavigation });
      const inner = this.querySelector("[data-hz-collection-navigation-inner]");
      inner?.classList.add("hz__collectionNavigationInner--ready");
    });
  }

  positionNavigationIndicator({ animate = false, center = false } = {}) {
    const viewport = this.querySelector("[data-hz-collection-navigation-viewport]");
    const inner = this.querySelector("[data-hz-collection-navigation-inner]");
    const indicator = this.querySelector("[data-hz-collection-navigation-indicator]");
    if (!viewport || !inner || !indicator) return;

    const activeLink =
      inner.querySelector('[data-hz-collection-navigation-link][aria-current="page"]') ||
      inner.querySelector(".hz__collectionNavigationLink--active[data-hz-collection-navigation-link]");

    this.updateNavigationSeparators(inner, activeLink);

    if (!activeLink) {
      indicator.style.width = "0px";
      indicator.style.transform = "translate3d(0, 0, 0)";
      return;
    }

    if (!animate) indicator.style.transition = "none";
    indicator.style.width = `${activeLink.offsetWidth}px`;
    indicator.style.transform = `translate3d(${activeLink.offsetLeft}px, 0, 0)`;

    if (!animate) {
      requestAnimationFrame(() => {
        if (indicator.isConnected) indicator.style.removeProperty("transition");
      });
    }

    if (center && viewport.scrollWidth > viewport.clientWidth + 1) {
      this.centerNavigationLink(viewport, activeLink);
    }
  }

  updateNavigationSeparators(inner, activeLink) {
    inner?.querySelectorAll(".hz__collectionNavigationSeparator").forEach((separator) => {
      const previousLink = separator.previousElementSibling;
      const nextLink = separator.nextElementSibling;
      const touchesActive = previousLink === activeLink || nextLink === activeLink;
      separator.classList.toggle("hz__collectionNavigationSeparator--concealed", Boolean(touchesActive));
    });
  }

  centerNavigationLink(viewport, link) {
    if (!viewport || !link) return;
    const desired = link.offsetLeft - (viewport.clientWidth - link.offsetWidth) / 2;
    const max = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    viewport.scrollTo({
      left: Math.max(0, Math.min(max, desired)),
      behavior: "auto"
    });
  }

  setSortMenuOpen(control, open, { focusOption = false, focusLast = false, restoreFocus = false } = {}) {
    if (!control) return;

    const trigger = control.querySelector("[data-hz-sort-trigger]");
    const menu = control.querySelector("[data-hz-sort-menu]");
    if (!trigger || !menu) return;

    if (open) {
      this.querySelectorAll('[data-hz-sort-trigger][aria-expanded="true"]').forEach((otherTrigger) => {
        if (otherTrigger === trigger) return;
        this.setSortMenuOpen(otherTrigger.closest("[data-hz-sort-control]"), false);
      });

      menu.hidden = false;
      trigger.setAttribute("aria-expanded", "true");
      control.classList.add("hz__collectionSortControl--open");

      if (focusOption) {
        queueMicrotask(() => {
          const options = Array.from(control.querySelectorAll("[data-hz-sort-option]"));
          if (!options.length) return;
          const selected = control.querySelector('[data-hz-sort-option][aria-selected="true"]');
          const target = focusLast ? options[options.length - 1] : selected || options[0];
          target?.focus({ preventScroll: true });
        });
      }
      return;
    }

    menu.hidden = true;
    trigger.setAttribute("aria-expanded", "false");
    control.classList.remove("hz__collectionSortControl--open");
    if (restoreFocus) trigger.focus({ preventScroll: true });
  }

  selectSortOption(option) {
    const control = option?.closest("[data-hz-sort-control]");
    const select = control?.querySelector("[data-hz-collection-sort]");
    const triggerLabel = control?.querySelector("[data-hz-sort-trigger-label]");
    const value = option?.dataset.hzSortValue;
    if (!control || !select || value == null) return;

    const changed = select.value !== value;
    select.value = value;

    control.querySelectorAll("[data-hz-sort-option]").forEach((candidate) => {
      candidate.setAttribute("aria-selected", candidate === option ? "true" : "false");
    });

    if (triggerLabel) triggerLabel.textContent = option.textContent.trim();
    this.setSortMenuOpen(control, false, { restoreFocus: true });

    if (changed) {
      select.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  handleDrawerClick = (event) => {
    const toggle = event.target.closest("[data-hz-filter-group-toggle]");
    if (toggle && this.drawer?.contains(toggle)) {
      const group = toggle.closest("[data-hz-filter-group]");
      if (group) this.setGroupOpen(group, !group.classList.contains("hz__collectionFilterGroup--open"));
      return;
    }

    const showMore = event.target.closest("[data-hz-filter-more]");
    if (showMore && this.drawer?.contains(showMore)) {
      const group = showMore.closest("[data-hz-filter-group]");
      if (!group) return;
      const expanded = group.classList.toggle("hz__collectionFilterGroup--showAll");
      showMore.setAttribute("aria-expanded", String(expanded));
      showMore.textContent = expanded
        ? showMore.dataset.hzShowLessLabel || "Show less"
        : showMore.dataset.hzShowMoreLabel || "Show more";
      return;
    }
  };

  handleDrawerChange = (event) => {
    if (!event.target.matches("[data-hz-filter-input]")) return;
    this.schedulePreview(90);
  };

  handleDrawerInput = (event) => {
    const searchInput = event.target.closest("[data-hz-filter-search]");
    if (searchInput) {
      this.filterLocalValues(searchInput);
      return;
    }

    if (event.target.matches(".hz__collectionPriceInput")) {
      this.schedulePreview(Number(this.dataset.hzPreviewDelay || 250));
    }
  };

  handleDrawerSubmit = (event) => {
    const form = event.target.closest("[data-hz-collection-filters-form]");
    if (!form || form !== this.drawerForm) return;
    event.preventDefault();
    this.applyDraft();
  };

  handleDrawerBeforeShow = () => {
    this.restoreAppliedDrawerState({ preserveOpenStates: true });
    this.clearDrawerStatus();
    this.updateApplyCount(this.totalCount);
  };

  handleDrawerAfterHide = () => {
    if (this.isApplying) return;
    this.previewController?.abort();
    this.previewController = null;
    this.previewResponse = null;
    this.restoreAppliedDrawerState({ preserveOpenStates: true });
    this.clearDrawerStatus();
  };

  handlePopState = () => {
    const url = new URL(window.location.href);
    this.commitUrl(url, { historyMode: "none", scroll: false, fresh: true });
  };

  isPlainPrimaryClick(event) {
    return (
      event.button === 0 &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.shiftKey &&
      !event.altKey
    );
  }

  cleanUrl(url) {
    const cleaned = new URL(url, window.location.href);
    cleaned.searchParams.delete("section_id");
    Array.from(cleaned.searchParams.entries()).forEach(([key, value]) => {
      if (value === "") cleaned.searchParams.delete(key);
    });
    return cleaned;
  }

  urlKey(url) {
    const normalized = this.cleanUrl(url);
    normalized.hash = "";
    return normalized.toString();
  }

  buildDraftUrl() {
    if (!this.drawerForm) return this.appliedUrl;

    const url = new URL(this.drawerForm.action, window.location.href);
    url.search = "";
    const formData = new FormData(this.drawerForm);

    for (const [key, rawValue] of formData.entries()) {
      const value = String(rawValue).trim();
      if (!value || key === "page") continue;
      url.searchParams.append(key, value);
    }

    url.searchParams.delete("page");
    return this.cleanUrl(url);
  }

  sectionRequestUrl(url) {
    const requestUrl = this.cleanUrl(url);
    requestUrl.searchParams.set("section_id", this.sectionId);
    return requestUrl;
  }

  schedulePreview(delay = 250) {
    window.clearTimeout(this.previewTimer);
    this.previewController?.abort();
    this.previewController = null;
    this.previewResponse = null;
    this.clearDrawerStatus();

    const draftUrl = this.buildDraftUrl();
    if (this.urlKey(draftUrl) === this.urlKey(this.appliedUrl)) {
      this.updateApplyCount(this.totalCount);
      this.setApplyBusy(false);
      return;
    }

    this.setApplyBusy(true);
    this.previewTimer = window.setTimeout(() => this.previewDraft(draftUrl), delay);
  }

  async previewDraft(draftUrl) {
    const sequence = ++this.previewSequence;
    const controller = new AbortController();
    this.previewController = controller;

    try {
      const sourceRoot = await this.fetchSectionRoot(draftUrl, controller.signal);
      if (sequence !== this.previewSequence || controller.signal.aborted) return;

      const total = Number(sourceRoot.dataset.hzCollectionTotal || 0);
      this.previewResponse = {
        key: this.urlKey(draftUrl),
        url: this.cleanUrl(draftUrl),
        root: sourceRoot
      };

      this.syncDrawerFromSource(sourceRoot, { commit: false, preserveOpenStates: true });
      this.updateApplyCount(total);
      this.setDrawerStatus(
        `${total} ${total === 1 ? this.dataset.hzProductsSingular : this.dataset.hzProductsPlural}`
      );
      this.setApplyBusy(false);
    } catch (error) {
      if (error?.name === "AbortError") return;
      this.previewResponse = null;
      this.setApplyBusy(false);
      this.setDrawerStatus(this.dataset.hzRequestError || "Unable to update products.", true);
      this.lastFailedAction = { type: "preview", url: this.cleanUrl(draftUrl) };
    } finally {
      if (this.previewController === controller) this.previewController = null;
    }
  }

  async applyDraft() {
    this.loadMoreController?.abort();
    this.loadMoreController = null;
    const draftUrl = this.buildDraftUrl();
    const sameAsApplied = this.urlKey(draftUrl) === this.urlKey(this.appliedUrl);

    if (sameAsApplied) {
      await this.drawer?.hide?.();
      return;
    }

    this.isApplying = true;
    this.setApplyBusy(true);
    this.clearDrawerStatus();

    try {
      let sourceRoot = null;
      if (this.previewResponse?.key === this.urlKey(draftUrl)) {
        sourceRoot = this.previewResponse.root;
      } else {
        const controller = new AbortController();
        this.commitController?.abort();
        this.commitController = controller;
        sourceRoot = await this.fetchSectionRoot(draftUrl, controller.signal);
      }

      await this.drawer?.hide?.();
      this.commitSource(sourceRoot, draftUrl, {
        historyMode: "push",
        scroll: true,
        focusTarget: "filters"
      });
      this.previewResponse = null;
    } catch (error) {
      if (error?.name !== "AbortError") {
        this.setDrawerStatus(this.dataset.hzRequestError || "Unable to update products.", true);
        this.lastFailedAction = { type: "apply", url: this.cleanUrl(draftUrl) };
      }
    } finally {
      this.isApplying = false;
      this.setApplyBusy(false);
    }
  }

  async commitUrl(
    url,
    { historyMode = "push", scroll = true, fresh = false, focusTarget = null } = {}
  ) {
    const cleanUrl = this.cleanUrl(url);
    const sequence = ++this.commitSequence;
    this.loadMoreController?.abort();
    this.loadMoreController = null;
    this.commitController?.abort();
    const controller = new AbortController();
    this.commitController = controller;

    this.setResultsBusy(true);
    this.hideRequestError();

    try {
      let sourceRoot = null;
      if (!fresh && this.previewResponse?.key === this.urlKey(cleanUrl)) {
        sourceRoot = this.previewResponse.root;
      } else {
        sourceRoot = await this.fetchSectionRoot(cleanUrl, controller.signal);
      }

      if (sequence !== this.commitSequence || controller.signal.aborted) return;
      this.commitSource(sourceRoot, cleanUrl, { historyMode, scroll, focusTarget });
      this.previewResponse = null;
    } catch (error) {
      if (error?.name === "AbortError") return;
      this.showRequestError(this.dataset.hzRequestError || "Unable to update products.");
      this.lastFailedAction = { type: "commit", url: cleanUrl, historyMode, scroll };
    } finally {
      if (this.commitController === controller) this.commitController = null;
      this.setResultsBusy(false);
    }
  }

  commitSource(
    sourceRoot,
    url,
    { historyMode = "push", scroll = true, focusTarget = null } = {}
  ) {
    const sourceDynamic = sourceRoot.querySelector("[data-hz-collection-dynamic]");
    const currentDynamic = this.querySelector("[data-hz-collection-dynamic]");
    if (!sourceDynamic || !currentDynamic) throw new Error("Collection response is missing its dynamic region.");

    this.destroyProductCardScopes(currentDynamic);
    currentDynamic.innerHTML = sourceDynamic.innerHTML;
    this.initProductCardScopes(currentDynamic);
    this.refreshEnhancedUi({ centerNavigation: false });

    this.totalCount = Number(sourceRoot.dataset.hzCollectionTotal || 0);
    this.visibleCount = Number(sourceRoot.dataset.hzCollectionVisible || 0);
    this.dataset.hzCollectionTotal = String(this.totalCount);
    this.dataset.hzCollectionVisible = String(this.visibleCount);

    this.syncDrawerFromSource(sourceRoot, { commit: true, preserveOpenStates: true });
    this.appliedUrl = this.cleanUrl(url);

    if (historyMode === "push") {
      window.history.pushState({ hzCollectionSection: this.sectionId }, "", this.appliedUrl);
    } else if (historyMode === "replace") {
      window.history.replaceState({ hzCollectionSection: this.sectionId }, "", this.appliedUrl);
    }

    this.hideRequestError();
    this.announce(
      `${this.totalCount} ${this.totalCount === 1 ? this.dataset.hzProductsSingular : this.dataset.hzProductsPlural}`
    );

    this.restoreCommitFocus(focusTarget);

    if (scroll && this.dataset.hzScrollOnUpdate === "true") {
      this.scrollToResults();
    }
  }

  async loadMore(link, { moveFocus = false } = {}) {
    if (
      !link?.href ||
      link.getAttribute("aria-busy") === "true" ||
      this.commitController ||
      this.isApplying
    ) return;

    this.loadMoreController?.abort();
    const controller = new AbortController();
    this.loadMoreController = controller;
    link.setAttribute("aria-busy", "true");
    link.setAttribute("aria-disabled", "true");
    this.hideRequestError();

    try {
      const sourceRoot = await this.fetchSectionRoot(new URL(link.href), controller.signal);
      const sourceScope = sourceRoot.querySelector("[data-hz-product-card-scope]");
      const currentScope = this.querySelector("[data-hz-product-card-scope]");
      const currentGrid = currentScope?.querySelector("[data-hz-collection-grid]");
      const sourceItems = Array.from(sourceScope?.querySelectorAll("[data-hz-collection-card-item]") || []);

      if (!currentScope || !currentGrid || !sourceItems.length) {
        throw new Error("The next collection page did not contain products.");
      }

      const existingIds = new Set(
        Array.from(currentGrid.querySelectorAll("[data-hz-collection-card-item]")).map(
          (item) => item.dataset.hzProductId
        )
      );
      const uniqueItems = sourceItems.filter((item) => !existingIds.has(item.dataset.hzProductId));

      this.destroyProductCardScopes(currentScope);
      const appendedItems = uniqueItems.map((item) => {
        const clone = item.cloneNode(true);
        if (!this.reducedMotion.matches) clone.classList.add("hz__collectionCardItem--new");
        currentGrid.appendChild(clone);
        return clone;
      });
      this.initProductCardScopes(currentScope);

      if (!this.reducedMotion.matches && appendedItems.length) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            appendedItems.forEach((item) => item.classList.add("hz__collectionCardItem--visible"));
          });
        });
        window.setTimeout(() => {
          appendedItems.forEach((item) => {
            if (!item.isConnected) return;
            item.classList.remove("hz__collectionCardItem--new", "hz__collectionCardItem--visible");
          });
        }, 520);
      }

      this.visibleCount += uniqueItems.length;
      this.dataset.hzCollectionVisible = String(this.visibleCount);

      const sourcePagination = sourceRoot.querySelector("[data-hz-collection-pagination]");
      const currentPagination = this.querySelector("[data-hz-collection-pagination]");
      if (sourcePagination && currentPagination) {
        currentPagination.replaceWith(sourcePagination.cloneNode(true));
      }

      this.querySelectorAll("[data-hz-visible-products]").forEach((node) => {
        node.textContent = String(this.visibleCount);
      });
      this.querySelectorAll("[data-hz-result-visible]").forEach((node) => {
        node.textContent = String(Math.min(this.totalCount, this.visibleCount));
      });

      this.announce(
        `${uniqueItems.length} ${uniqueItems.length === 1 ? this.dataset.hzProductsSingular : this.dataset.hzProductsPlural} added. ${this.visibleCount} shown.`
      );

      if (moveFocus && appendedItems.length) {
        const firstFocusable = appendedItems[0].querySelector(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        firstFocusable?.focus({ preventScroll: true });
      }
    } catch (error) {
      if (error?.name === "AbortError") return;
      this.showRequestError(this.dataset.hzRequestError || "Unable to load more products.");
      this.lastFailedAction = { type: "loadMore", url: new URL(link.href) };
    } finally {
      if (this.loadMoreController === controller) this.loadMoreController = null;
      if (link.isConnected) {
        link.removeAttribute("aria-busy");
        link.removeAttribute("aria-disabled");
      }
    }
  }

  async fetchSectionRoot(url, signal) {
    const response = await fetch(this.sectionRequestUrl(url), {
      method: "GET",
      credentials: "same-origin",
      cache: "no-store",
      headers: {
        Accept: "text/html",
        "X-Requested-With": "XMLHttpRequest"
      },
      signal
    });

    if (!response.ok) {
      throw new Error(`Collection request failed with status ${response.status}.`);
    }

    const html = await response.text();
    const documentFragment = new DOMParser().parseFromString(html, "text/html");
    const sourceRoot = documentFragment.querySelector(
      `${HZ_COLLECTION_SELECTOR}[data-hz-collection-section="${CSS.escape(this.sectionId)}"]`
    );

    if (!sourceRoot) {
      throw new Error("Collection response did not contain the requested section.");
    }

    return sourceRoot;
  }

  syncDrawerFromSource(sourceRoot, { commit = false, preserveOpenStates = true } = {}) {
    if (!this.drawer) return;

    const sourceForm = sourceRoot.querySelector("[data-hz-collection-filters-form]");
    const currentForm = this.drawer.querySelector("[data-hz-collection-filters-form]");
    if (!sourceForm || !currentForm) return;

    const openStates = preserveOpenStates ? this.captureOpenStates(currentForm) : new Map();
    const showAllStates = this.captureShowAllStates(currentForm);
    const searchStates = this.captureSearchStates(currentForm);
    const activeElement = document.activeElement;
    const focusDescriptor = this.focusDescriptor(activeElement);
    const drawerBody = this.drawer.shadowRoot?.querySelector('[part="body"]');
    const scrollTop = drawerBody?.scrollTop || 0;

    currentForm.action = sourceForm.action;
    currentForm.innerHTML = sourceForm.innerHTML;
    this.drawerForm = currentForm;

    this.applyOpenStates(currentForm, openStates);
    this.applyShowAllStates(currentForm, showAllStates);
    this.applySearchStates(currentForm, searchStates);
    this.drawer.setAttribute("data-hz-collection-ready", "true");

    if (focusDescriptor) {
      queueMicrotask(() => {
        const replacement = this.findFocusTarget(currentForm, focusDescriptor);
        replacement?.focus({ preventScroll: true });
      });
    }
    if (drawerBody) drawerBody.scrollTop = scrollTop;

    if (commit) this.captureAppliedDrawerState();
  }

  captureAppliedDrawerState() {
    if (!this.drawerForm) return;
    this.appliedFormHTML = this.drawerForm.innerHTML;
    this.appliedFormAction = this.drawerForm.action;
    this.appliedOpenStates = this.captureOpenStates(this.drawerForm);
  }

  restoreAppliedDrawerState({ preserveOpenStates = true } = {}) {
    if (!this.drawerForm || this.appliedFormHTML == null) return;
    const currentOpenStates = preserveOpenStates
      ? this.captureOpenStates(this.drawerForm)
      : this.appliedOpenStates || new Map();

    this.drawerForm.innerHTML = this.appliedFormHTML;
    if (this.appliedFormAction) this.drawerForm.action = this.appliedFormAction;
    this.applyOpenStates(this.drawerForm, currentOpenStates);
    this.drawer?.setAttribute("data-hz-collection-ready", "true");
  }

  captureOpenStates(form) {
    const states = new Map();
    form?.querySelectorAll("[data-hz-filter-group]").forEach((group) => {
      states.set(
        group.dataset.hzFilterParam || "",
        group.classList.contains("hz__collectionFilterGroup--open")
      );
    });
    return states;
  }

  applyOpenStates(form, states) {
    form?.querySelectorAll("[data-hz-filter-group]").forEach((group) => {
      const key = group.dataset.hzFilterParam || "";
      if (states.has(key)) this.setGroupOpen(group, states.get(key), false);
    });
  }

  captureShowAllStates(form) {
    const states = new Map();
    form?.querySelectorAll("[data-hz-filter-group]").forEach((group) => {
      states.set(
        group.dataset.hzFilterParam || "",
        group.classList.contains("hz__collectionFilterGroup--showAll")
      );
    });
    return states;
  }

  applyShowAllStates(form, states) {
    form?.querySelectorAll("[data-hz-filter-group]").forEach((group) => {
      const key = group.dataset.hzFilterParam || "";
      const expanded = states.get(key) || false;
      group.classList.toggle("hz__collectionFilterGroup--showAll", expanded);
      const button = group.querySelector("[data-hz-filter-more]");
      if (button) {
        button.setAttribute("aria-expanded", String(expanded));
        button.textContent = expanded
          ? button.dataset.hzShowLessLabel || "Show less"
          : button.dataset.hzShowMoreLabel || "Show more";
      }
    });
  }

  setGroupOpen(group, open, focus = true) {
    group.classList.toggle("hz__collectionFilterGroup--open", Boolean(open));
    const button = group.querySelector("[data-hz-filter-group-toggle]");
    button?.setAttribute("aria-expanded", String(Boolean(open)));
    if (focus) button?.focus({ preventScroll: true });
  }

  filterLocalValues(searchInput) {
    const group = searchInput.closest("[data-hz-filter-group]");
    if (!group) return;
    const query = searchInput.value.trim().toLocaleLowerCase();
    group.classList.toggle("hz__collectionFilterGroup--showAll", Boolean(query));

    group.querySelectorAll("[data-hz-filter-value-row]").forEach((row) => {
      const label = row.dataset.hzFilterValueLabel || "";
      row.dataset.hzSearchHidden = String(Boolean(query) && !label.includes(query));
    });

    const moreButton = group.querySelector("[data-hz-filter-more]");
    if (moreButton) moreButton.hidden = Boolean(query);
  }

  captureSearchStates(form) {
    const states = new Map();
    form?.querySelectorAll("[data-hz-filter-search]").forEach((input) => {
      const group = input.closest("[data-hz-filter-group]");
      const key = group?.dataset.hzFilterParam || "";
      if (key && input.value) states.set(key, input.value);
    });
    return states;
  }

  applySearchStates(form, states) {
    if (!states?.size) return;
    form?.querySelectorAll("[data-hz-filter-search]").forEach((input) => {
      const group = input.closest("[data-hz-filter-group]");
      const key = group?.dataset.hzFilterParam || "";
      if (!states.has(key)) return;
      input.value = states.get(key);
      this.filterLocalValues(input);
    });
  }

  focusDescriptor(element) {
    if (!(element instanceof HTMLInputElement)) return null;
    if (element.matches("[data-hz-filter-search]")) {
      const group = element.closest("[data-hz-filter-group]");
      return { type: "search", group: group?.dataset.hzFilterParam || "" };
    }
    if (!element.name) return null;
    return { type: "input", name: element.name, value: element.value };
  }

  findFocusTarget(form, descriptor) {
    if (descriptor.type === "search") {
      return Array.from(form.querySelectorAll("[data-hz-filter-group]")).find(
        (group) => group.dataset.hzFilterParam === descriptor.group
      )?.querySelector("[data-hz-filter-search]") || null;
    }

    return Array.from(form.querySelectorAll("input[name]")).find(
      (input) => input.name === descriptor.name && input.value === descriptor.value
    ) || null;
  }

  updateApplyCount(count) {
    const button = this.drawer?.querySelector("[data-hz-collection-apply]");
    const label = button?.querySelector("[data-hz-collection-apply-label]");
    if (!label) return;

    const noun = Number(count) === 1
      ? this.dataset.hzProductsSingular || "Product"
      : this.dataset.hzProductsPlural || "Products";
    label.textContent = `${this.dataset.hzShowPrefix || "Show"} ${Number(count) || 0} ${noun}`;
  }

  setApplyBusy(busy) {
    const button = this.drawer?.querySelector("[data-hz-collection-apply]");
    if (!button) return;
    button.toggleAttribute("disabled", Boolean(busy));
    if (busy) {
      button.setAttribute("aria-busy", "true");
    } else {
      button.removeAttribute("aria-busy");
    }
  }

  setResultsBusy(busy) {
    this.classList.toggle("hz__collectionProducts--loading", Boolean(busy));
    const results = this.querySelector("[data-hz-collection-results]");
    results?.setAttribute("aria-busy", String(Boolean(busy)));
  }

  setDrawerStatus(message, isError = false) {
    const status = this.drawer?.querySelector("[data-hz-drawer-status]");
    if (!status) return;
    status.textContent = message || "";
    status.toggleAttribute("data-hz-error", Boolean(isError));
  }

  clearDrawerStatus() {
    this.setDrawerStatus("");
  }

  showRequestError(message) {
    const region = this.querySelector("[data-hz-collection-request-message]");
    const text = region?.querySelector("[data-hz-collection-request-message-text]");
    if (!region || !text) return;
    text.textContent = message;
    region.hidden = false;
  }

  hideRequestError() {
    const region = this.querySelector("[data-hz-collection-request-message]");
    if (region) region.hidden = true;
  }

  handleRetry = () => {
    const action = this.lastFailedAction;
    if (!action) return;
    this.lastFailedAction = null;

    if (action.type === "preview") {
      this.previewDraft(action.url);
    } else if (action.type === "apply") {
      this.applyDraft();
    } else if (action.type === "loadMore") {
      const link = this.querySelector(`[data-hz-collection-load-more][href="${CSS.escape(action.url.pathname + action.url.search)}"]`)
        || this.querySelector("[data-hz-collection-load-more]");
      if (link) this.loadMore(link);
    } else {
      this.commitUrl(action.url, {
        historyMode: action.historyMode || "none",
        scroll: action.scroll ?? false,
        fresh: true
      });
    }
  };

  announce(message) {
    const local = this.querySelector("[data-hz-collection-announcement]");
    if (local) {
      local.textContent = "";
      window.setTimeout(() => {
        if (local.isConnected) local.textContent = message;
      }, 30);
    }

    const global = document.getElementById("status-announcement");
    if (global) global.textContent = message;
  }


  restoreCommitFocus(target) {
    if (!target) return;

    queueMicrotask(() => {
      let element = null;
      if (target === "sort") {
        element = this.querySelector("[data-hz-sort-trigger]") || this.querySelector("[data-hz-collection-sort]");
      } else if (target === "filters") {
        element = this.querySelector("[data-hz-filter-trigger]");
      } else if (target === "results") {
        element = this.querySelector("[data-hz-collection-results]");
      }
      element?.focus({ preventScroll: true });
    });
  }

  scrollToResults() {
    const target = this.querySelector("[data-hz-collection-toolbar]") || this;
    const behavior = this.reducedMotion.matches ? "auto" : "smooth";
    const top = target.getBoundingClientRect().top + window.scrollY - 24;
    window.scrollTo({ top: Math.max(0, top), behavior });
  }

  destroyProductCardScopes(container) {
    const scopes = [];
    if (container.matches?.("[data-hz-product-card-scope]")) scopes.push(container);
    container.querySelectorAll?.("[data-hz-product-card-scope]").forEach((scope) => scopes.push(scope));
    scopes.forEach((scope) => {
      scope.dispatchEvent(
        new CustomEvent("shopify:section:unload", {
          bubbles: true,
          detail: { hzCollectionUpdate: true }
        })
      );
    });
  }

  initProductCardScopes(container) {
    const scopes = [];
    if (container.matches?.("[data-hz-product-card-scope]")) scopes.push(container);
    container.querySelectorAll?.("[data-hz-product-card-scope]").forEach((scope) => scopes.push(scope));
    scopes.forEach((scope) => {
      scope.dispatchEvent(
        new CustomEvent("shopify:section:load", {
          bubbles: true,
          detail: { hzCollectionUpdate: true }
        })
      );
    });
  }

  abortPendingRequests() {
    this.previewController?.abort();
    this.commitController?.abort();
    this.loadMoreController?.abort();
    this.previewController = null;
    this.commitController = null;
    this.loadMoreController = null;
  }
}

if (!customElements.get("ar-collection-products")) {
  customElements.define("ar-collection-products", ArCollectionProducts);
}

// Retry buttons live inside the dynamic region and are replaced after every
// successful update, so one delegated document listener is the most stable
// lifecycle-safe integration point.
document.addEventListener("click", (event) => {
  const retry = event.target.closest("[data-hz-collection-retry]");
  if (!retry) return;
  const section = retry.closest(HZ_COLLECTION_SELECTOR);
  section?.handleRetry?.();
});
